/* Timberustic smooth lightbox swipe for Enterprise 2.0.0. */
(() => {
  if (window.timberusticGallerySwipeLoaded) return;
  window.timberusticGallerySwipeLoaded = true;
  const bound = new Map();
  function bind(gallery) {
    if (bound.has(gallery)) return;
    if (!gallery.thumbContainer || !gallery.currentTransform) return;
    const controller = new AbortController();
    const listen = (target, type, handler, options = {}) => target.addEventListener(type, handler, {
      ...(typeof options === "boolean" ? {capture: options} : options), signal: controller.signal
    });
    const surface = gallery.querySelector('.gallery-zoom__zoom-container');
    const pointers = new Set();
    let gesture, layer, busy = false, suppressUntil = 0;
    const reduced = matchMedia('(prefers-reduced-motion: reduce)');
    const fit = () => {
      const img = gallery.currentZoomImage;
      return img?.naturalWidth && gallery.currentTransform.zoom <= Math.min(1,
        gallery.clientWidth / img.naturalWidth, gallery.clientHeight / img.naturalHeight) + .01;
    };
    function clearLayer() {
      layer?.remove(); layer = null; busy = false;
      const image = gallery.currentZoomImage;
      if (image?.complete && image.naturalWidth && image.style.transform) {
        image.style.visibility = '';
      }
    }
    function cancel() { gesture = null; clearLayer(); }
    function neighbours() {
      const thumbs = [...gallery.thumbContainer.children].filter(t => !t.hidden);
      const index = thumbs.findIndex(t => t.classList.contains('gallery-zoom__thumb--active'));
      return [thumbs[index - 1], thumbs[index], thumbs[index + 1]];
    }
    function buildLayer() {
      layer = document.createElement('div');
      layer.className = 'timberustic-swipe-layer';
      gesture.thumbs = neighbours();
      gesture.thumbs.forEach((thumb, index) => {
        if (!thumb) return;
        const panel = document.createElement('div');
        panel.className = 'timberustic-swipe-panel';
        panel.style.left = `${(index - 1) * 100}%`;
        const image = document.createElement('img');
        image.src = thumb.dataset.zoomUrl;
        image.alt = ''; image.draggable = false;
        panel.append(image); layer.append(panel);
      });
      // Keep the transition cover outside the node replaced by selectThumb().
      gallery.append(layer);
      gallery.currentZoomImage.style.visibility = 'hidden';
    }
    function paint(dx) {
      const canMove = dx < 0 ? gesture.thumbs[2] : gesture.thumbs[0];
      gesture.offset = canMove ? dx : dx * .22;
      layer.style.transform = `translate3d(${gesture.offset}px,0,0)`;
    }
    listen(surface, 'dragstart', e => e.preventDefault());
    listen(surface, 'pointerdown', e => {
      pointers.add(e.pointerId);
      if (pointers.size > 1) { suppressUntil = Date.now() + 500; cancel(); return; }
      if (busy || e.button !== 0) return;
      gesture = {id:e.pointerId, x:e.clientX, y:e.clientY, fit:fit(), moved:false,
        horizontal:false, width:gallery.clientWidth, offset:0};
    });
    listen(surface, 'pointermove', e => {
      if (!gesture || gesture.id !== e.pointerId || pointers.size !== 1) return;
      const dx = e.clientX - gesture.x, dy = e.clientY - gesture.y;
      if (Math.hypot(dx,dy) > 8) gesture.moved = true;
      if (!gesture.fit) return; // Existing Enterprise panning handles zoomed images.
      if (!gesture.horizontal) {
        if (Math.abs(dy) > 12 && Math.abs(dy) > Math.abs(dx)) { gesture.fit = false; return; }
        if (Math.abs(dx) < 8 || Math.abs(dx) < Math.abs(dy) * 1.2) return;
        gesture.horizontal = true;
        surface.setPointerCapture(e.pointerId);
        buildLayer();
      }
      paint(dx);
    });
    function finish(e) {
      pointers.delete(e.pointerId);
      if (!gesture || gesture.id !== e.pointerId) return;
      const g = gesture;
      if (g.moved) suppressUntil = Date.now() + 500;
      if (!g.horizontal || e.type === 'pointercancel') { cancel(); return; }
      busy = true;
      const dx = e.clientX - g.x;
      const candidate = dx < 0 ? g.thumbs[2] : g.thumbs[0];
      const target = candidate && Math.abs(dx) >= Math.min(120, g.width * .22) ? candidate : null;
      const destination = target ? (dx < 0 ? -g.width : g.width) : 0;
      gesture = null;
      const duration = reduced.matches ? 0 : 230;
      layer.style.transition = `transform ${duration}ms cubic-bezier(.22,.7,.25,1)`;
      layer.style.transform = `translate3d(${destination}px,0,0)`;
      const settlingLayer = layer;
      setTimeout(() => {
        if (layer !== settlingLayer) return;
        if (!gallery.isConnected) { clearLayer(); return; }
        if (!target) { clearLayer(); return; }
        gallery.selectThumb(target);
        const image = gallery.currentZoomImage;
        // A cached image can be complete before its onload handler has fitted it.
        // Keep the incoming panel visible until the real image is decoded and fitted.
        const ready = async () => {
          try { await image.decode(); } catch {
            if (layer === settlingLayer) clearLayer();
            return;
          }
          if (layer !== settlingLayer || !gallery.isConnected || gallery.currentZoomImage !== image) return;
          gallery.setInitialImagePosition();
          requestAnimationFrame(() => {
            if (layer === settlingLayer && gallery.currentZoomImage === image) clearLayer();
          });
        };
        ready();
      }, duration);
      if (surface.hasPointerCapture(e.pointerId)) surface.releasePointerCapture(e.pointerId);
    }
    listen(surface, 'pointerup', finish);
    listen(surface, 'pointercancel', finish);
    listen(surface, 'click', e => {
      if (busy || Date.now() < suppressUntil) { e.preventDefault(); e.stopImmediatePropagation(); }
    }, true);
    ['mousemove','touchmove','wheel'].forEach(type => listen(surface, type, e => {
      if (gesture?.horizontal || busy) { e.preventDefault(); e.stopPropagation(); }
    }, {passive:false}));
    listen(gallery, 'click', e => {
      if (e.target.closest('button') && !e.target.closest('.gallery-zoom__zoom-container')) cancel();
    }, true);
    listen(window, 'resize', cancel);
    // Preload adjacent full-size images so the incoming panel is ready while dragging.
    const preload = () => neighbours().forEach(t => {
      if (t && !t.dataset.timberusticPreloaded) {
        t.dataset.timberusticPreloaded = 'true';
        const img = new Image(); img.src = t.dataset.zoomUrl;
      }
    });
    const preloadObserver = new MutationObserver(preload);
    preloadObserver.observe(gallery.thumbContainer,{subtree:true,attributes:true,attributeFilter:['class']});
    bound.set(gallery, () => {
      cancel(); pointers.clear(); preloadObserver.disconnect();
      controller.abort();
    });
    preload();
  }
  const scan = () => {
    for (const [gallery, cleanup] of bound) {
      if (!gallery.isConnected) { cleanup(); bound.delete(gallery); }
    }
    document.querySelectorAll('.gallery-zoom-modal gallery-zoom').forEach(bind);
  };
  new MutationObserver(scan).observe(document.body,{childList:true,subtree:true});
  customElements.whenDefined('gallery-zoom').then(scan);
  scan();
})();
